// Una fila de la tabla: la cabecera de la carga, plegable, con su detalle de
// líneas debajo.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chevron } from '@/components/common/Chevron'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { MarcaCelda, useMarcada } from '@/components/common/MarcasFilas'
import { CargaDocumentosDrawer, EMPRESAS_CON_PACKING } from '@/components/pedidos-ventas/CargaDocumentosDrawer'
import type { CargaSalida, MarcasFilas } from '@/types'
import { CHAPA_RIESGO, clasesFilaCarga, enRiesgo, estadoPickingDe } from '@/utils/cargasSalida'
import type { ColumnaCargaSalida, ContextoColumnas } from './columnasCargaSalida'
import { DetalleCargaSalida } from './DetalleCargaSalida'
import { ChatCargaDrawer } from './ChatCargaDrawer'

interface Props {
  c: CargaSalida
  dark: boolean
  columnas: ColumnaCargaSalida[]
  hoy: Date
  abierta: boolean
  onToggle: () => void
  marcas: MarcasFilas
  clave: string
  documentosAbiertos: boolean
  onDocumentos: (abrir: boolean) => void
}

export function FilaCargaSalida({
  c, dark, columnas, hoy, abierta, onToggle, marcas, clave,
  documentosAbiertos, onDocumentos,
}: Props) {
  const { t } = useTranslation()
  const marcada = useMarcada(marcas, clave)
  const [chatAbierto, setChatAbierto] = useState(false)
  const ctx: ContextoColumnas = { t, dark, hoy }
  const riesgo = enRiesgo(c, hoy)
  const numPedido = c.pedidos[0]
  const disponibleDocs = EMPRESAS_CON_PACKING.has(c.empresa.toLowerCase())

  const tinte = clasesFilaCarga(c, dark, marcada, hoy)
  const hover = dark ? 'hover:bg-white/[0.04]' : 'hover:bg-white/70'
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'
  const divider = dark ? 'border-white/8' : 'border-slate-200'
  const tdBase = 'px-2 py-1.5 text-[11px] align-middle whitespace-nowrap'

  return (
    <>
      <tr className={`border-t ${divider} transition-colors duration-150 ${marcada ? marcadoBg : (tinte.fondo || hover)} ${tinte.franja}`}>
        <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
        <td className={tdBase}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={abierta}
            className="flex items-center gap-1.5 cursor-pointer min-w-0"
          >
            <Chevron abierto={abierta} className={`w-3 h-3 shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`} />
            <span className={`font-mono text-[11px] font-semibold truncate ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
              {c.carga}
            </span>
            {riesgo && (
              <span
                title={t('cargasSalida.estado.atRisk')}
                className={`shrink-0 text-[8px] font-bold px-1 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
                  tinte.alarma ? CHAPA_RIESGO.sobreAlarma : (dark ? CHAPA_RIESGO.oscuro : CHAPA_RIESGO.claro)
                }`}
              >
                {t('cargasSalida.estado.atRisk')}
              </span>
            )}
          </button>
        </td>
        {columnas.map(col => (
          <td key={col.key} className={`${tdBase} ${col.alinear === 'right' ? 'text-right tabular-nums' : ''}`}>
            {col.render(c, ctx)}
          </td>
        ))}
        <td className={`${tdBase} text-right`}>
          <button
            type="button"
            onClick={() => setChatAbierto(true)}
            aria-label={t('cargasSalida.comentarios.abrir', { carga: c.carga })}
            title={t('cargasSalida.comentarios.abrir', { carga: c.carga })}
            className={`inline-flex p-1.5 rounded-md cursor-pointer transition-colors ${
              dark ? 'text-slate-500 hover:text-sky-300 hover:bg-white/8' : 'text-slate-400 hover:text-sky-600 hover:bg-slate-200/70'
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onDocumentos(true)}
            disabled={!disponibleDocs}
            aria-label={t('cargasSalida.acciones.documentos', { carga: c.carga })}
            title={disponibleDocs
              ? t('cargasSalida.acciones.documentos', { carga: c.carga })
              : t('cargasSalida.acciones.documentosNoDisponible', { empresa: c.empresa.toUpperCase() })}
            className={`inline-flex p-1.5 rounded-md transition-colors ${
              disponibleDocs
                ? `cursor-pointer ${dark ? 'text-slate-500 hover:text-sky-300 hover:bg-white/8' : 'text-slate-400 hover:text-sky-600 hover:bg-slate-200/70'}`
                : `cursor-not-allowed opacity-40 ${dark ? 'text-slate-600' : 'text-slate-400'}`
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6M9 15h6M9 11h3" />
            </svg>
          </button>
        </td>
      </tr>

      {abierta && (
        <SubTableWrapper dark={dark} colSpan={columnas.length + 3}>
          <DetalleCargaSalida c={c} dark={dark} />
        </SubTableWrapper>
      )}

      {chatAbierto && (
        <ChatCargaDrawer carga={c.carga} dark={dark} onCerrar={() => setChatAbierto(false)} />
      )}

      {documentosAbiertos && numPedido && (
        <CargaDocumentosDrawer
          dark={dark}
          empresa={c.empresa}
          numPedido={numPedido}
          carga={c.carga}
          estado={estadoPickingDe(c)}
          paletizada={c.paletizada}
          aerea={c.aerea}
          contextoAlbaran={{ pedido: numPedido, destinatario: c.clienteNombre, destino: c.destino }}
          onClose={() => onDocumentos(false)}
        />
      )}
    </>
  )
}
